import { BannerActionButton } from "@/components/conversations/magic-moment/banner-action-button";
import { CollapsibleBanner } from "@/components/conversations/magic-moment/collapsible-banner";
import type { DeepThoughtsNudgeState } from "@/lib/api/mentorships";
import { useUpdateDeepThoughtsNudgeState } from "@/lib/api/mentorships";
import { useDeepThoughtsSendStore } from "@/lib/hooks/conversations/use-deep-thoughts-send-store";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship";
import { useFeatureFlagEnabled } from "@posthog/react";
import { Sparkles, Sprout } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";

function isExpired(nudge: DeepThoughtsNudgeState): boolean {
  if (!nudge.expires_at) return false;
  const expiresAt = DateTime.fromISO(nudge.expires_at);
  return expiresAt.isValid && expiresAt < DateTime.now();
}

export function DeepThoughtsNudgeBanner() {
  const { mentorship } = useGetChannelMentorship();
  const deepThoughtsEnabled =
    useFeatureFlagEnabled("activity_deep_thoughts") || import.meta.env.DEV;
  const openSend = useDeepThoughtsSendStore((s) => s.openSend);
  const { mutate: updateNudgeState } = useUpdateDeepThoughtsNudgeState();

  const nudge = mentorship?.metadata?.deep_thoughts_nudge;
  const mentorshipId = mentorship?.id;

  const [expanded, setExpanded] = useState(() => !nudge?.banner_collapsed);

  if (
    !deepThoughtsEnabled ||
    !mentorshipId ||
    !nudge ||
    nudge.banner_dismissed ||
    isExpired(nudge)
  ) {
    return null;
  }

  const patchNudge = (fields: { banner_collapsed?: boolean }) => {
    updateNudgeState({ mentorshipId, fields });
  };

  return (
    <CollapsibleBanner
      title="Opportunity"
      icon={<Sprout className="h-6 w-6 text-text-primary" strokeWidth={1.75} />}
      expanded={expanded}
      onToggle={() => {
        const next = !expanded;
        setExpanded(next);
        patchNudge({ banner_collapsed: !next });
      }}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm leading-5 text-text-secondary">
          You&apos;ve done Hot Takes and Connection cards together — they might
          be ready to go deeper
        </p>
        <div className="flex flex-wrap gap-x-1">
          <BannerActionButton
            icon={<Sparkles className="h-3 w-3" strokeWidth={1.75} />}
            label="Send Deep Thoughts"
            onClick={() => openSend()}
          />
        </div>
      </div>
    </CollapsibleBanner>
  );
}
